import React, { useState, useEffect, useRef } from 'react';
import './FeesSetting.css';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const FeesSetting = () => {
  const { selectedCenter, userData } = useUser();
  const token = localStorage.getItem('auth-token');
  const [formData, setFormData] = useState({
    tournamentCommission: '',
    courtBookingCommission: '',
    feesCommission: '',
    gstNumber: '',
    panCardNumber: '',
    coachingEnabled: false,
    tournamentEnabled: false,
    bookAndPlayEnabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const oldPayloadRef = useRef(null);

  const normalize = (fd = formData) => ({
    academyId: Number(selectedCenter) || null,
    settingMap: {
      TOURNAMENT_COMMISSION: String(fd.tournamentCommission ?? ''),
      COURT_BOOKING_COMMISSION: String(fd.courtBookingCommission ?? ''),
      FEE_COMMISSION: String(fd.feesCommission ?? ''),
      COACHING_ENABLED: fd.coachingEnabled ? 'true' : 'false',
      TOURNAMENT_ENABLED: fd.tournamentEnabled ? 'true' : 'false',
      COURT_BOOKING_ENABLED: fd.bookAndPlayEnabled ? 'true' : 'false',
      GST_NUMBER: String(fd.gstNumber ?? ''),
      PAN_CARD_NUMBER: String(fd.panCardNumber ?? ''),
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedCenter) return;
      try {
        const url = `${process.env.REACT_APP_API_ACADEMY_SETTINGS}/${selectedCenter}/settings`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch settings data');
        }

        const result = await response.json();
        const { settingMap = {} } = result?.data?.academySettingForm || {};

        const next = {
          tournamentCommission: settingMap.TOURNAMENT_COMMISSION || '',
          courtBookingCommission: settingMap.COURT_BOOKING_COMMISSION || '',
          feesCommission: settingMap.FEE_COMMISSION || '',
          gstNumber: settingMap.GST_NUMBER || '',
          panCardNumber: settingMap.PAN_CARD_NUMBER || '',
          coachingEnabled: String(settingMap.COACHING_ENABLED) === 'true',
          tournamentEnabled: String(settingMap.TOURNAMENT_ENABLED) === 'true',
          bookAndPlayEnabled: String(settingMap.COURT_BOOKING_ENABLED) === 'true',
        };
        setFormData(next);

        oldPayloadRef.current = normalize(next);
      } catch (error) {
        console.error('Error fetching settings data:', error);
        oldPayloadRef.current = normalize({
          tournamentCommission: '',
          courtBookingCommission: '',
          feesCommission: '',
          gstNumber: '',
          panCardNumber: '',
          coachingEnabled: false,
          tournamentEnabled: false,
          bookAndPlayEnabled: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCenter]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const url = `${process.env.REACT_APP_API_ACADEMY_SETTINGS}/${selectedCenter}/settings`;
    const newPayload = normalize(formData);
    const oldPayload = oldPayloadRef.current || normalize({});

    const { digest, html } = buildDiffPreview(oldPayload, newPayload);
    if (!digest.length) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to update',
        text: 'No differences detected.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Apply these changes?',
      html,
      showCancelButton: true,
      confirmButtonText: 'Update',
    });
    if (!confirm.isConfirmed) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newPayload),
      });
      const result = await res.json();

      if (!res.ok || result.success === false) {
        throw new Error(result.success_message || result.message || 'Failed to submit form data');
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Settings updated successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      fireAudit({
        userId: userData?.userId,
        entity: 'academy_settings',
        affected_id: String(newPayload.academyId ?? ''),
        action: 'update',
        section: 'Academy Profile',
        subSection: 'Fees Setting',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload,
        newPayload,
        extra: { page: 'FeesSetting' },
      });
      oldPayloadRef.current = newPayload;
    } catch (error) {
      console.error('Error submitting form:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'An error occurred while submitting the form.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="enrollment-container">
      <form onSubmit={handleSubmit} className="fees-form">
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="tournamentCommission"
            min="0"
            className={`walkin-form ${formData.tournamentCommission ? 'has-value' : ''}`}
            value={formData.tournamentCommission}
            onChange={handleChange}
            placeholder="Enter Tournament Commission (%)"
            required
            onWheel={e => e.target.blur()}
            onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.tournamentCommission && (
            <label htmlFor="tournamentCommission">Tournament Commission (%)</label>
          )}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="courtBookingCommission"
            min="0"
            className={`walkin-form ${formData.courtBookingCommission ? "has-value" : ""}`}
            value={formData.courtBookingCommission}
            onChange={handleChange}
            placeholder="Enter Court Booking Commission (%)"
            required
            onWheel={e => e.target.blur()}
            onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.courtBookingCommission && (
            <label htmlFor="courtBookingCommission">Court Booking Commission (%)</label>
          )}
        </div>


        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="feesCommission"
            min="0"
            className={`walkin-form ${formData.feesCommission ? "has-value" : ""}`}
            value={formData.feesCommission}
            onChange={handleChange}
            placeholder="Enter Fees Commission (%)"
            required
            onWheel={e => e.target.blur()}
            onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.feesCommission && (
            <label htmlFor="feesCommission">Fees Commission (%)</label>
          )}
        </div>


        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="gstNumber"
            className={`walkin-form ${formData.gstNumber ? "has-value" : ""}`}
            value={formData.gstNumber}
            onChange={handleChange}
            placeholder="Enter GST Number"
          />
          {formData.gstNumber && (
            <label htmlFor="gstNumber">GST Number</label>
          )}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="panCardNumber"
            className={`walkin-form ${formData.panCardNumber ? "has-value" : ""}`}
            value={formData.panCardNumber}
            onChange={handleChange}
            placeholder="Enter PAN Card Number"
          />
          {formData.panCardNumber && (
            <label htmlFor="panCardNumber">PAN Card Number</label>
          )}
        </div>


        <div className="checkbox-group">
          <label>
            <input
              type="checkbox"
              name="coachingEnabled"
              checked={formData.coachingEnabled}
              onChange={handleChange}
            />
            Coaching Enabled
          </label>

          <label>
            <input
              type="checkbox"
              name="bookAndPlayEnabled"
              checked={formData.bookAndPlayEnabled}
              onChange={handleChange}
            />
            Book and Play Enabled
          </label>
        </div>

        <button type="submit" className="submit-button" disabled={isSubmitting || loading}>
          {isSubmitting ? 'Updating…' : 'Update'}
        </button>
      </form>
    </div>
  );
};

export default FeesSetting;
